import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Badge, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getTeamStandings, TeamStanding, TeamStandingsResponse, addTeamsToStandings, AddTeamsToStandingsRequest, editStandings, EditStandingsRequest, removeTeamFromStandings, RemoveTeamFromStandingsRequest, editTeamsInStandings, EditTeamsInStandingsRequest, getTeamStanding } from '../../api/tournament/standingsService';
import { listTeams } from '../../api/tournament/teamService';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import TeamManagementCard from '../../Common/TeamManagementCard';
import TeamStandingsTable from '../../Common/TeamStandingsTable';
import { DeleteModal } from '../../Common/DeleteModal';
import '../../Common/TournamentHeader.css';
import '../../Common/ManageTournamentsFixturesTable.css';
import '../../components/DashboardStyles.css';
import { getImageUrl } from '../../utils/imageUrl';
import { hasTournamentWriteAccess } from '../../utils/roleBasedAccess';

const SingleStandingsDetailPage: React.FC = () => {
  const { tournamentId, ageCategoryId } = useParams<{ tournamentId: string; ageCategoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const canManageStandings = hasTournamentWriteAccess(
    { ...user, subRole: role, permissions },
    ['tournament.fixtures.manage']
  );
  
  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Add/Edit Teams Card State
  const [showAddTeamsCard, setShowAddTeamsCard] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<any[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [addingTeams, setAddingTeams] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Edit Team Modal State
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamStanding | null>(null);
  const [editForm, setEditForm] = useState({
    played: '',
    won: '',
    lost: '',
    drawn: '',
    goalsFor: '',
    goalsAgainst: '',
    points: '',
    isQualified: false
  });
  const [editing, setEditing] = useState(false);
  
  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<TeamStanding | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState(false);
  
  // Get age category info, view mode, tournamentSeasonId, and fromTournamentDetailPage flag from location state
  const ageCategoryInfo = location.state?.ageCategoryInfo;
  const isViewMode = location.state?.viewMode || false;
  const tournamentSeasonId = location.state?.tournamentSeasonId; // Get tournamentSeasonId for back navigation
  const fromTournamentDetailPage = location.state?.fromTournamentDetailPage || false; // Check if we came from tournament detail page (default season)
  
  // Get sport from location state/URL to preserve it during navigation
  let currentSport: string | undefined;
  try {
    currentSport = getSportValueUtil(null, location);
  } catch {
    // If sport cannot be determined, try to get from URL params
    const urlParams = new URLSearchParams(location.search);
    currentSport = urlParams.get('sport') || undefined;
  }
  
  // Preserve selectedTournament from location state for navigation
  const selectedTournament = (location.state as any)?.selectedTournament || (currentSport ? { sports: currentSport } : undefined);

  // Qualified options for dropdown
  const qualifiedOptions = [
    { value: true, label: 'Qualified' },
    { value: false, label: 'Not Qualified' }
  ];

  useEffect(() => {
    if (tournamentId && ageCategoryId) {
      loadStandings();
    }
  }, [tournamentId, ageCategoryId]);

  // Set view mode from location state
  useEffect(() => {
    setViewMode(isViewMode);
  }, [isViewMode]);

  const loadStandings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const sport = getSportValueUtil(null, location);
      const seasonId = tournamentSeasonId || new URLSearchParams(location.search).get('seasonId') || undefined;
      const response = await getTeamStandings(ageCategoryId!, sport, seasonId);
      
      // Check if response exists and has the expected structure
      if (!response) {
        setError('No response received from server');
        return;
      }
      
      // The API returns the data directly as an array
      if (Array.isArray(response)) {
        // Handle empty data array
        if (response.length === 0) {
          setStandings([]);
          return;
        }
        
        // Check if it's single standings (has team property) or group standings
        const isSingleStandings = response[0].hasOwnProperty('team');
        
        if (isSingleStandings) {
          setStandings(response as TeamStanding[]);
        } else {
          setError('Expected single standings but received group standings');
        }
      } else {
        setError(`Invalid response format. Expected array but received: ${typeof response}`);
      }
    } catch (err: any) {
      console.error('Error loading standings:', err);
      if (err.response) {
        setError(`API Error: ${err.response.status} - ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('Network Error: Unable to reach server');
      } else {
        setError(`Error: ${err.message || 'Failed to load standings'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load available teams for the age category
  const loadAvailableTeams = async (editMode?: boolean) => {
    if (!tournamentId || !ageCategoryInfo?.ageCategory) return;
    
    try {
      setTeamsLoading(true);
      
      // Debug logging
      console.log('=== Teams API Debug ===');
      console.log('tournamentId:', tournamentId);
      console.log('ageCategoryInfo:', ageCategoryInfo);
      console.log('ageCategory being used:', ageCategoryInfo.ageCategory);
      
      // Get teams for the specific age category
      console.log('Getting teams for specific age category...');
      
      const sport = getSportValueUtil(null, location);
      
      const response = await listTeams({
        tournamentId: tournamentId,
        ageCategory: ageCategoryInfo.ageCategory, // Use the actual age category name (e.g., "U7")
        page: 1,
        pageSize: 100, // Get all teams for this age category
        tournamentSeason: tournamentSeasonId || undefined, // Pass tournamentSeasonId if available
        sport: sport // Pass sport parameter
      });
      
      console.log('Teams API response:', response);
      console.log('Paginated results:', response.paginatedResults);
      console.log('========================');
      
      if (response.paginatedResults) {
        // Handle paginated response structure - data is directly in response
        const allTeams = response.paginatedResults;
        
        // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
        // Transform teams to use the nested team object
        const teamsToUse = allTeams.map((teamItem: any) => {
          const teamData = teamItem.team || teamItem; // Use nested team object if available
          return {
            _id: teamData._id || teamItem._id, // Use team._id (actual team ID) first, fallback to _id
            name: teamData.name || teamItem.name || '',
            shortName: teamData.shortName || teamItem.shortName || '',
            logo: teamData.logo || teamItem.logo || null,
            // Keep original structure for reference
            originalTeam: teamItem
          };
        });
        
        // For edit mode, include all teams (both existing and available)
        // For add mode, filter out teams that are already in standings
        // Filter out standings with null teams first
        const validStandings = standings.filter(standing => standing && standing.team && standing.team._id);
        const existingTeamIds = validStandings.map(standing => standing.team._id);
        let availableTeams;
        
        const currentEditMode = editMode !== undefined ? editMode : isEditMode;
        
        if (currentEditMode) {
          // In edit mode, include all teams so user can see and manage existing teams
          // If no teams found from API, at least show the existing teams from standings
          if (teamsToUse.length > 0) {
            availableTeams = teamsToUse;
          } else {
            // Fallback: create options from existing standings teams (only valid ones)
            availableTeams = validStandings.map(standing => ({
              _id: standing.team._id,
              name: standing.team.name || 'Unknown Team',
              shortName: standing.team.shortName || '',
              logo: standing.team.logo || null
            }));
          }
        } else {
          // In add mode, only show teams not already in standings
          availableTeams = teamsToUse.filter((team: any) => !existingTeamIds.includes(team._id));
        }
        
        setAvailableTeams(availableTeams);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  // Handle opening the add teams card
  const handleOpenAddTeamsCard = () => {
    setIsEditMode(false);
    setShowAddTeamsCard(true);
    setSelectedTeams([]);
    loadAvailableTeams();
  };

  // Handle opening the edit teams card
  const handleOpenEditTeamsCard = () => {
    setIsEditMode(true);
    setShowAddTeamsCard(true);
    
    // Don't pre-select existing teams in the dropdown
    // They will be shown in the list below instead
    setSelectedTeams([]);
    
    // Load all teams for edit mode - pass true to indicate edit mode
    loadAvailableTeams(true);
  };

  // Handle team selection for React-Select
  const handleTeamSelection = (selectedOptions: any) => {
    setSelectedTeams(selectedOptions || []);
  };

  // Handle adding/updating teams to standings
  const handleAddTeams = async () => {
    if (selectedTeams.length === 0) return;
    
    try {
      setAddingTeams(true);
      
      // Get only newly selected teams (not existing ones)
      const selectedTeamData = selectedTeams.map(team => ({
        id: team.value,
        name: team.label
      }));

      const requestData: AddTeamsToStandingsRequest = {
        tournamentCategoryId: ageCategoryId!,
        teams: selectedTeamData
      };

      await addTeamsToStandings(requestData, currentSport);
      
      // Refresh standings data
      await loadStandings();
      
      // Show success message
      toast.success(`${isEditMode ? 'Teams updated' : 'Teams added'} successfully!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Close card and reset state
      setShowAddTeamsCard(false);
      setSelectedTeams([]);
      setIsEditMode(false);
      
    } catch (err: any) {
      console.error('Error adding teams:', err);
      
      // Show error message to user
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update teams';
      toast.error(`Error: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setAddingTeams(false);
    }
  };

  // Handle opening edit team modal
  const handleEditTeam = async (team: TeamStanding) => {
    try {
      setEditingTeam(team);
      
      // Fetch the latest team standing data
      const teamData = await getTeamStanding(
        ageCategoryId!,
        team.team._id,
        undefined,
        getSportValueUtil(null, location)
      );
      
      // Update the edit form with fetched data
      setEditForm({
        played: String(teamData.data?.played || team.played),
        won: String(teamData.data?.won || team.won),
        lost: String(teamData.data?.lost || team.lost),
        drawn: String(teamData.data?.drawn || team.drawn),
        goalsFor: String(teamData.data?.goalsFor || team.goalsFor),
        goalsAgainst: String(teamData.data?.goalsAgainst || team.goalsAgainst),
        points: String(teamData.data?.points || team.points),
        isQualified: teamData.data?.isQualified || team.isQualified || false
      });
      
      setShowEditTeamModal(true);
    } catch (err: any) {
      console.error('Error fetching team data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch team data';
      toast.error(`Error: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Handle editing team standings
  const handleEditTeamStandings = async () => {
    if (!editingTeam) return;
    
    try {
      setEditing(true);
      
      const requestData: EditStandingsRequest = {
        categoryId: ageCategoryId!,
        teamId: editingTeam.team._id,
        updates: {
          played: parseInt(editForm.played) || 0,
          won: parseInt(editForm.won) || 0,
          lost: parseInt(editForm.lost) || 0,
          drawn: parseInt(editForm.drawn) || 0,
          goalsFor: parseInt(editForm.goalsFor) || 0,
          goalsAgainst: parseInt(editForm.goalsAgainst) || 0,
          points: parseInt(editForm.points) || 0,
          isQualified: editForm.isQualified,
        },
      };

      await editStandings(requestData, getSportValueUtil(null, location));
      
      // Refresh standings data
      await loadStandings();
      
      // Show success message
      toast.success('Team standings updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Close modal and reset state
      setShowEditTeamModal(false);
      setEditingTeam(null);
      
    } catch (err: any) {
      console.error('Error updating team standings:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update team standings';
      toast.error(`Error: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setEditing(false);
    }
  };

  // Handle removing team from standings
  const handleRemoveTeam = async (team: TeamStanding) => {
    setTeamToDelete(team);
    setShowDeleteModal(true);
  };

  // Handle actual team deletion
  const handleConfirmDelete = async () => {
    if (!teamToDelete) return;
    
    try {
      setDeleting(true);
      
      const requestData: RemoveTeamFromStandingsRequest = {
        tournamentId: tournamentId!,
        ageCategoryId: ageCategoryId!,
        teamId: teamToDelete.team._id
        // No groupName or groupId for single standings
      };

      await removeTeamFromStandings(requestData, getSportValueUtil(null, location));
      
      // Refresh standings data
      await loadStandings();
      
      // Show success message
      toast.success(`${teamToDelete.team.name} has been removed from the standings.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Close modal
      setShowDeleteModal(false);
      setTeamToDelete(null);
      
    } catch (err: any) {
      console.error('Error removing team:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove team';
      toast.error(`Error removing team: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setDeleting(false);
    }
  };



  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="table" rows={10} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'Manage Tournaments', onClick: () => navigate('/manage-tournaments-fixtures') },
              { 
                label: 'Tournament Details', 
                onClick: () => {
                  // If from tournament detail page (default season), navigate back to tournament detail page
                  // Otherwise, navigate to season detail page if tournamentSeasonId exists
                  const navigationState: any = {
                    activeTab: 'standings',
                    selectedTournament: selectedTournament,
                    allTournaments: (location.state as any)?.allTournaments
                  };
                  
                  if (fromTournamentDetailPage || !tournamentSeasonId) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: navigationState
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
                      state: navigationState
                    });
                  }
                }
              },
              { label: `${ageCategoryInfo?.ageCategory || 'Age Category'} - Single Standings`, active: true }
            ]}
          />
        </div>

        {/* Header - Match TournamentDetailPage styling */}
        <div style={{ padding: '0 15px' }}>
          <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
              <button 
                className="back-button"
                onClick={() => {
                  // If from tournament detail page (default season), navigate back to tournament detail page
                  // Otherwise, navigate to season detail page if tournamentSeasonId exists
                  // Ensure selectedTournament always has sports property for TournamentDetailPage
                  const finalSelectedTournament = selectedTournament?.sports 
                    ? selectedTournament 
                    : (currentSport ? { sports: currentSport } : (selectedTournament || { sports: '' }));
                  
                  const navigationState: any = {
                    activeTab: 'standings',
                    selectedTournament: finalSelectedTournament,
                    allTournaments: (location.state as any)?.allTournaments
                  };
                  
                  if (fromTournamentDetailPage || !tournamentSeasonId) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: navigationState
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
                      state: navigationState
                    });
                  }
                }}
                style={{ position: 'relative', zIndex: 10 }}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="tournament-logo-small" style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
                <div className="default-tournament-logo-small">
                  <i className="ri-trophy-line"></i>
                </div>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>
                  {ageCategoryInfo?.ageCategory || 'Age Category'} - Single Standings
                </h1>
                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                  <span className="tournament-short-name-badge">Single</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '0 15px' }}>
          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="ri-trophy-line me-2"></i>
                Team Standings
              </h5>
              <Button
                variant="primary"
                onClick={standings.length > 0 ? handleOpenEditTeamsCard : handleOpenAddTeamsCard}
                disabled={viewMode || !canManageStandings}
                className="d-flex align-items-center"
              >
                <i className={standings.length > 0 ? "ri-edit-line me-2" : "ri-add-line me-2"}></i>
                {standings.length > 0 ? 'Edit Teams' : 'Add Teams'}
              </Button>
            </Card.Header>
            <Card.Body>
              {/* Error Message */}
              {error && (
                <div className="alert alert-danger mb-3">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </div>
              )}

              {/* Add/Edit Teams Card - Above Table */}
              {showAddTeamsCard && (
                <div className="mb-4">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom-0">
                      <h5 className="mb-0 fw-semibold">
                        <i className={isEditMode ? "ri-edit-circle-line me-2 text-primary" : "ri-add-circle-line me-2 text-primary"}></i>
                        {isEditMode ? 'Edit Teams' : 'Add Teams'}
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-4">
                            <Form.Label className="fw-medium">
                              Teams<span className="text-danger ms-1">*</span>
                            </Form.Label>
                            {teamsLoading ? (
                              <div className="text-center py-3">
                                <ShimmerLoader type="table" rows={2} />
                              </div>
                            ) : availableTeams.length > 0 ? (
                              <div>
                                {isEditMode ? (
                                  // Edit Mode: Show separate sections for existing and available teams
                                  <div>

                                    {/* Add New Teams Section - Moved to top */}
                                    <div className="mb-4">
                                      <h6 className="fw-semibold text-success mb-3">
                                        <i className="ri-add-circle-line me-2"></i>
                                        Add New Teams to Standings
                                      </h6>
                                      <Select
                                        classNamePrefix="react-select"
                                        isMulti
                                        options={availableTeams.filter(team => 
                                          !standings
                                            .filter(standing => standing && standing.team && standing.team._id)
                                            .some(standing => standing.team._id === team._id)
                                        ).map(team => ({
                                          value: team._id,
                                          label: team.name,
                                          shortName: team.shortName
                                        }))}
                                        value={selectedTeams}
                                        onChange={handleTeamSelection}
                                        placeholder="Search and select teams to add..."
                                        isSearchable
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                        formatOptionLabel={(option: any) => (
                                          <div>
                                            <div className="fw-medium">{option.label}</div>
                                            <small className="text-muted">{option.shortName}</small>
                                          </div>
                                        )}
                                        styles={{
                                          control: (base) => ({ 
                                            ...base, 
                                            minHeight: '45px',
                                            border: '2px solid #e0e0e0',
                                            borderRadius: '8px',
                                            boxShadow: 'none'
                                          }),
                                          menu: (base) => ({ 
                                            ...base, 
                                            zIndex: 99999,
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0
                                          }),
                                          multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: '#d4edda',
                                            border: '1px solid #c3e6cb',
                                            borderRadius: '6px'
                                          }),
                                          multiValueLabel: (base) => ({
                                            ...base,
                                            color: '#155724',
                                            fontSize: '14px'
                                          }),
                                          multiValueRemove: (base) => ({
                                            ...base,
                                            color: '#155724',
                                            '&:hover': {
                                              backgroundColor: '#155724',
                                              color: 'white'
                                            }
                                          })
                                        }}
                                      />
                                      {selectedTeams.length > 0 && (
                                        <div className="mt-2">
                                          <small className="text-success">
                                            <i className="ri-add-line me-1"></i>
                                            {selectedTeams.length} team(s) selected to add
                                          </small>
                          </div>
                            )}
                          </div>

                                    {/* Currently in Standings Section - Moved below */}
                                    <div>
                                      <h6 className="fw-semibold text-primary mb-3">
                                        <i className="ri-trophy-line me-2"></i>
                                        Currently in Standings ({standings.length} teams)
                                      </h6>
                                      <div className="border rounded p-3 bg-light">
                                        {standings.length > 0 ? (
                                          <div className="row g-2">
                                            {standings
                                              .filter(standing => standing && standing.team && standing.team._id)
                                              .map((standing, index) => (
                                              <div key={standing.team._id} className="col-md-6 col-lg-4">
                                                <div className="d-flex align-items-center justify-content-between p-2 bg-white rounded border">
                                                  <div className="d-flex align-items-center">
                            {standing.team.logo ? (
                              <img
                                src={getImageUrl(standing.team.logo)}
                                alt={standing.team.name || 'Team logo'}
                                                        className="rounded-circle me-2"
                                                        style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                                          // Show the fallback icon when image fails to load
                                                          const fallbackIcon = target.parentElement?.querySelector('.team-fallback-icon') as HTMLElement;
                                                          if (fallbackIcon) {
                                                            fallbackIcon.style.display = 'flex';
                                                          }
                                                        }}
                                                      />
                                                    ) : (
                                                      <div 
                                                        className="rounded-circle bg-light border d-flex align-items-center justify-content-center me-2 team-fallback-icon" 
                              style={{
                                                          width: '32px', 
                                                          height: '32px',
                                                          display: 'flex'
                                                        }}
                                                      >
                                                        <i className="ri-team-line text-muted" style={{ fontSize: '16px' }}></i>
                            </div>
                                                    )}
                            <div>
                                                      <div className="fw-medium text-dark">{standing.team.name || 'Unknown Team'}</div>
                              <small className="text-muted">{standing.team.shortName || ''}</small>
                            </div>
                                                  </div>
                                                  <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveTeam(standing)}
                                                    className="ms-2"
                                                    title="Remove from standings"
                                                  >
                                                    <i className="ri-delete-bin-line"></i>
                                                  </Button>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="text-center text-muted py-4">
                                            <div className="mb-3">
                                              <i className="ri-trophy-line" style={{ fontSize: '48px', opacity: 0.2, color: '#6c757d' }}></i>
                                            </div>
                                            <h6 className="text-muted mb-2">No Teams in Standings</h6>
                                            <p className="mb-0">No teams are currently in the standings</p>
                                          </div>
                                        )}
              </div>
        </div>
            </div>
          ) : (
                                  // Add Mode: Show regular dropdown
            <div>
                  <Select
                    classNamePrefix="react-select"
                    isMulti
                    options={availableTeams.map(team => ({
                      value: team._id,
                      label: team.name,
                      shortName: team.shortName
                    }))}
                    value={selectedTeams}
                    onChange={handleTeamSelection}
                                      placeholder="Select teams to add..."
                    isSearchable
                    isClearable
                                      menuPortalTarget={document.body}
                                      menuPosition="absolute"
                    formatOptionLabel={(option: any) => (
                      <div>
                        <div className="fw-medium">{option.label}</div>
                        <small className="text-muted">{option.shortName}</small>
                      </div>
                    )}
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px',
                                          border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                                        menu: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999,
                                          position: 'absolute',
                                          top: '100%',
                                          left: 0,
                                          right: 0
                                        }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: '#e7f1ff',
                        border: '1px solid #b3d7ff',
                        borderRadius: '6px'
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: '#0d6efd',
                        fontSize: '14px'
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: '#0d6efd',
                        '&:hover': {
                          backgroundColor: '#0d6efd',
                          color: 'white'
                        }
                      })
                    }}
                  />
                                  </div>
                                )}
                </div>
              ) : (
                <div className="text-center py-4">
                                <div className="mb-3">
                                  <i className="ri-team-line" style={{ fontSize: '48px', opacity: 0.2, color: '#6c757d' }}></i>
                                </div>
                                <h6 className="text-muted mb-2">
                                  {teamsLoading ? 'Loading Teams...' : 
                                   isEditMode ? 
                                   'No Teams Available' :
                                   standings.length > 0 ? 
                                   'All Teams Added' : 
                                   'No Teams Available'}
                                </h6>
                                <p className="text-muted mb-3">
                                  {teamsLoading ? 'Please wait while we load the teams...' : 
                                   isEditMode ? 
                                   'No teams are available for this age category' :
                     standings.length > 0 ? 
                     'All teams for this age category are already in the standings' : 
                                   'No teams are available for this age category'}
                  </p>
                                {!teamsLoading && standings.length > 0 && !isEditMode && (
                    <small className="text-muted">
                                    <i className="ri-check-circle-line me-1"></i>
                      {standings.length} team(s) already added to standings
                    </small>
                  )}
                                {!teamsLoading && isEditMode && availableTeams.length === 0 && (
                                  <small className="text-muted">
                                    <i className="ri-information-line me-1"></i>
                                    Please check if teams are properly assigned to this age category
                                  </small>
              )}
            </div>
          )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setShowAddTeamsCard(false);
                            setSelectedTeams([]);
                            setIsEditMode(false);
                          }}
                          className="px-4"
                          style={{ borderRadius: '8px', minHeight: '40px' }}
                        >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddTeams}
            disabled={selectedTeams.length === 0 || addingTeams}
                          className="px-4"
                          style={{ borderRadius: '8px', minHeight: '40px' }}
          >
            {addingTeams ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {isEditMode ? 'Saving...' : 'Adding...'}
              </>
            ) : (
              <>
                              <i className={isEditMode ? "ri-save-line me-2" : "ri-add-line me-2"}></i>
                              {selectedTeams.length > 0 ? `Add ${selectedTeams.length} Team${selectedTeams.length !== 1 ? 's' : ''}` : 'Add Teams'}
                            </>
                          )}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              )}

              {/* Standings Table */}
              <TeamStandingsTable
                standings={standings}
                viewMode={viewMode || !canManageStandings}
                onEditTeam={handleEditTeam}
                onRemoveTeam={handleRemoveTeam}
              />

            </Card.Body>
          </Card>
        </div>
      </Container>


      {/* Edit Team Modal */}
      <Modal show={showEditTeamModal} onHide={() => setShowEditTeamModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-edit-line me-2"></i>
            Edit Team Standings - {editingTeam?.team.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTeam && (
            <div>
              <div className="mb-4">
                <div className="d-flex align-items-center">
                  {editingTeam.team.logo ? (
                    <img
                      src={getImageUrl(editingTeam.team.logo)}
                      alt={editingTeam.team.name}
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #e9ecef',
                        marginRight: '15px'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.setAttribute('style', 'display: flex');
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#f8f9fa',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      display: editingTeam.team.logo ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#6c757d',
                      marginRight: '15px'
                    }}
                  >
                    {editingTeam.team.name.charAt(0)}
                  </div>
                  <div>
                    <h5 className="mb-0">{editingTeam.team.name}</h5>
                    <small className="text-muted">{editingTeam.team.shortName}</small>
                  </div>
                </div>
              </div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Matches Played</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.played}
                      onChange={(e) => setEditForm({ ...editForm, played: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Wins</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.won}
                      onChange={(e) => setEditForm({ ...editForm, won: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Draws</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.drawn}
                      onChange={(e) => setEditForm({ ...editForm, drawn: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Losses</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.lost}
                      onChange={(e) => setEditForm({ ...editForm, lost: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Goals For</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.goalsFor}
                      onChange={(e) => setEditForm({ ...editForm, goalsFor: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Goals Against</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.goalsAgainst}
                      onChange={(e) => setEditForm({ ...editForm, goalsAgainst: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Points</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.points}
                      onChange={(e) => setEditForm({ ...editForm, points: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="1"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Qualified Status</Form.Label>
                    <Select
                      value={qualifiedOptions.find(option => option.value === editForm.isQualified)}
                      onChange={(selectedOption) => setEditForm({ ...editForm, isQualified: selectedOption?.value || false })}
                      options={qualifiedOptions}
                      isSearchable={false}
                      placeholder="Select qualification status"
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditTeamModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleEditTeamStandings}
            disabled={editing}
          >
            {editing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              <>
                <i className="ri-save-line me-2"></i>
                Update Standings
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setTeamToDelete(null);
        }}
        deleteModalFunction={handleConfirmDelete}
        title="Remove Team from Standings"
        message={`Are you sure you want to remove ${teamToDelete?.team.name} from the standings? This action cannot be undone.`}
        confirmLabel="Yes, Remove"
        cancelLabel="Cancel"
        isLoading={deleting}
      />
    </div>
  );
};

export default SingleStandingsDetailPage;
